/*
 * Copyright © 2026. Legalbird eine Marke der Legalbird GmbH
 */

import React from "react";
import { AbstractCase } from "../../types/Entities/AbstractCase";
import InquiryToCustomer, { InquiryToCustomerStatus } from "../../types/Entities/InquiryToCustomer";
import { Button, Stack, SxProps, Theme } from "@mui/material";
import { Link } from "react-router";
import { getCaseLink } from "../../services/productService";
import dayjs from "dayjs";
import { useInquiryToCustomers } from "@/src/hooks/queries/inquiryToCustomer";
import AlertLegalbird from "@/src/components/legalbird/AlertLegalbird/AlertLegalbird";
import AlertContentLegalbird from "@/src/components/legalbird/AlertLegalbird/AlertContentLegalbird";

type ProductStatusInquiriesToCustomerProps = {
  product: AbstractCase;
  sx?: SxProps<Theme>;
};

export default function ProductStatusInquiriesToCustomer({ product, sx }: ProductStatusInquiriesToCustomerProps) {
  const inquiryFilters = {
    productId: product.id,
    "status[0]": InquiryToCustomerStatus.STATUS_OPEN,
    "status[1]": InquiryToCustomerStatus.STATUS_OVERDUE,
  };
  const { data: inquiries } = useInquiryToCustomers(inquiryFilters);

  if (!inquiries?.length) {
    return null;
  }

  return (
    <Stack spacing={2} sx={sx}>
      {inquiries
        .sort((a, b) => (dayjs(a.deadline).isAfter(dayjs(b.deadline)) ? 1 : -1))
        .map((inquiry) => (
          <InquiryToCustomerEntry key={inquiry.id} product={product} inquiryToCustomer={inquiry} />
        ))}
    </Stack>
  );
}

type InquiryToCustomerEntryProps = {
  product: AbstractCase;
  inquiryToCustomer: InquiryToCustomer;
};
const InquiryToCustomerEntry = ({ inquiryToCustomer, product }: InquiryToCustomerEntryProps) => {
  if (!inquiryToCustomer) {
    return null;
  }

  let text;
  let buttonText;

  switch (inquiryToCustomer.inquiryType) {
    case "appointment":
      text = "In Ihrem Fall ist eine telefonische Rücksprache notwendig. Bitte vereinbaren Sie einen Beratungstermin.";
      buttonText = "Termin vereinbaren";
      break;
    case "informationAndDocuments":
      text = "Bitte melden Sie sich mit den angefragten Informationen und Dokumenten bei uns zurück, damit Ihr Fall bearbeitet werden kann.";
      buttonText = "Rückmeldung geben";
      break;
    case "documents":
      text = "Bitte melden Sie sich mit den angefragten Dokumenten bei uns zurück, damit Ihr Fall bearbeitet werden kann.";
      buttonText = "Rückmeldung geben";
      break;
    case "link":
      text = "Bitte geben Sie uns Rückmeldung, damit Ihr Fall weiter bearbeitet werden kann.";
      buttonText = "Rückmeldung geben";
      break;
    case "information":
    default:
      text = "Bitte melden Sie sich mit den angefragten Informationen bei uns zurück, damit Ihr Fall bearbeitet werden kann.";
      buttonText = "Rückmeldung geben";
      break;
  }

  return (
    <AlertLegalbird severity={"warning"}>
      <AlertContentLegalbird
        title={"Rückmeldung geben"}
        subtitle={text}
        actions={
          <Button
            fullWidth
            size={"small"}
            variant={"outlined"}
            color={"warning"}
            component={Link}
            to={getCaseLink(product) + "/anfrage/" + inquiryToCustomer.id}
          >
            {buttonText}
          </Button>
        }
      />
    </AlertLegalbird>
  );
};
